import { inject, Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AppsyncService } from './appsync.service';
import { DocumentNode } from 'graphql';
import { TC_CLAIMS_HEADER } from '../constants/auth-constants';
import { AuthApiService } from './api/auth-api.service';
import { FetchPolicy } from '@apollo/client';
import { getGraphQLField } from '../graphql/graphql-response';

@Injectable({
  providedIn: 'root',
})
export class BaseAppSyncService {
  protected _api: AppsyncService = inject(AppsyncService);
  protected _authApi: AuthApiService = inject(AuthApiService);

  /**
   * Helper function that abstracts away the underlying query logic needed and performs the query
   * @param graphql - graphql DocumentNode object for the query (see graphql-tag for help)
   * @param variables - object of variables that you want to pass to the query, key in this object must match the name of the graphql variable you want to match
   * @param builderFn - function that will build the returned object into an appropriate type rather than the generic T - sometimes needs to be passed via arrow function to preserve `this`
   * @param graphqlQueryName - name of the graphql query, needed to parse it from the graphql response
   * @param fetchPolicy - graphQL apollo fetchPolicy, defaults to no-cache
   * @returns - Observable with the return type of builderFn
   */
  protected query<T, TPayload>(
    graphql: DocumentNode,
    variables: object,
    builderFn: (input: TPayload) => T,
    graphqlQueryName: string,
    fetchPolicy: FetchPolicy = 'no-cache'
  ): Observable<T> {
    return from(this._api.gqlClient())
      .pipe(
        switchMap((client) => {
          return client.query<Record<string, TPayload>>({
            query: graphql,
            variables: variables,
            context: {
              headers: {
                [TC_CLAIMS_HEADER]: this._authApi.getUserClaimsHeader(),
              },
            },
            fetchPolicy: fetchPolicy,
          });
        })
      )
      .pipe(
        map((result) => {
          return builderFn(getGraphQLField(result.data, graphqlQueryName));
        })
      );
  }

  /**
   * Helper function that abstracts away the underlying mutation logic needed and performs the mutation
   * @param graphql - graphql DocumentNode object for the mutation (see graphql-tag for help)
   * @param variables - object of variables that you want to pass to the mutation, key in this object must match the name of the graphql variable you want to match
   * @param builderFn - function that will build the returned object into an appropriate type rather than the generic T - sometimes needs to be passed via arrow function to preserve `this`
   * @param graphqlMutationName - name of the graphql mutation, needed to parse it from the graphql response
   * @returns - Observable with the return type of builderFn
   */
  protected mutate<T, TPayload>(
    graphql: DocumentNode,
    variables: object,
    builderFn: (input: TPayload) => T,
    graphqlMutationName: string
  ): Observable<T> {
    return from(this._api.gqlClient())
      .pipe(
        switchMap((client) => {
          return client.mutate<Record<string, TPayload>>({
            mutation: graphql,
            variables: variables,
            context: {
              headers: {
                [TC_CLAIMS_HEADER]: this._authApi.getUserClaimsHeader(),
              },
            },
            fetchPolicy: 'no-cache',
          });
        })
      )
      .pipe(
        map((result) => {
          return builderFn(getGraphQLField(result.data, graphqlMutationName));
        })
      );
  }
}
